import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Link, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider, useLang } from "./i18n/LanguageContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Catalog from "./pages/Catalog";
import Cart from "./pages/Cart";
import AdminApprovals from "./pages/AdminApprovals";
import AdminProducts from "./pages/AdminProducts";
import AdminInventory from "./pages/AdminInventory";
import AdminBarcodeMapping from "./pages/AdminBarcodeMapping";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminOrders from "./pages/AdminOrders";
import AdminCustomers from "./pages/AdminCustomers";
import AdminPromotions from "./pages/AdminPromotions";
import AdminPricing from "./pages/AdminPricing";
import UpdateNotifier from "./components/UpdateNotifier";
import MyOrders from "./pages/MyOrders";
import PresentationSheet from "./pages/PresentationSheet";
import PackingSheet from "./pages/PackingSheet";
import BulkPackingSheets from "./pages/BulkPackingSheets";
import { logout } from "./services/authService";

const IDLE_LOGOUT_MS = 30 * 60 * 1000; // auto log-off after 30 min inactivity

// Signs the user out after 30 minutes without any interaction (Brian,
// 2026-07-13). Activity = clicks, keys, scrolling, touching. On logout,
// ProtectedRoute bounces the user to /login automatically.
function IdleLogout() {
  const { profile } = useAuth();
  useEffect(() => {
    if (!profile) return;
    let lastActivity = Date.now();
    const bump = () => { lastActivity = Date.now(); };
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach(ev => window.addEventListener(ev, bump, { passive: true }));
    const timer = setInterval(() => {
      if (Date.now() - lastActivity > IDLE_LOGOUT_MS) logout();
    }, 60 * 1000);
    return () => {
      events.forEach(ev => window.removeEventListener(ev, bump));
      clearInterval(timer);
    };
  }, [profile]);
  return null;
}

// "/" lands logged-in users on the catalog, everyone else on the login page.
function HomeRedirect() {
  const { profile, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={profile ? "/catalog" : "/login"} replace />;
}

// Total cases in the cart. Quantities come in 0.25 increments, so trim any
// trailing decimals (2.5 stays 2.5, 3.0 shows as 3).
function formatCount(n) {
  return Number.isInteger(n) ? n : parseFloat(n.toFixed(2));
}

function NavBar({ cartCount }) {
  const { profile } = useAuth();
  const { t, lang, toggleLang } = useLang();
  // Mobile: links collapse behind a hamburger; every link tap closes the menu.
  const [menuOpen, setMenuOpen] = useState(false);
  if (!profile) return null;
  const close = () => setMenuOpen(false);
  const isStaff = profile.role === "rep" || profile.role === "admin";
  const isAdmin = profile.role === "admin";
  return (
    <nav className="navbar">
      {/* Brand = shortcut to the catalog (Brian, 2026-07-14) */}
      <Link to="/catalog" className="nav-brand" onClick={close}>{t("appName")}</Link>
      {/* Language toggle — always visible, shows the language you'd SWITCH TO */}
      <button className="lang-toggle" onClick={toggleLang}>
        {lang === "en" ? "한국어" : "English"}
      </button>
      {/* Always-visible cart shortcut — one tap to the cart, no menu needed. */}
      <Link className="cart-icon-link" to="/cart" onClick={close} aria-label="Cart">
        🛒
        {cartCount > 0 && <span className="cart-badge">{formatCount(cartCount)}</span>}
      </Link>
      <button
        className="nav-toggle"
        aria-label="Menu"
        onClick={() => setMenuOpen(o => !o)}
      >
        {menuOpen ? "✕" : "☰"}
      </button>
      {/* Links grouped by workflow (Brian, 2026-07-13): place the order →
          fulfill today's orders → manage the store. Labels show in the mobile
          dropdown; thin dividers separate the groups on desktop. */}
      <div className={`nav-links${menuOpen ? " open" : ""}`}>
        <span className="nav-section-label">{t("navSecOrdering")}</span>
        <Link to="/catalog" onClick={close}>{t("navCatalog")}</Link>
        <Link to="/my-orders" onClick={close}>{t("navMyOrders")}</Link>

        {isStaff && (
          <>
            <span className="nav-divider" aria-hidden="true" />
            <span className="nav-section-label">{t("navSecOps")}</span>
            <Link to="/admin/orders" onClick={close}>{t("navOrders")}</Link>
            <Link to="/admin/barcodes" onClick={close}>{t("navBarcodes")}</Link>
            <Link to="/presentation" onClick={close}>{t("navPresentation")}</Link>

            <span className="nav-divider" aria-hidden="true" />
            <span className="nav-section-label">{t("navSecManage")}</span>
            <Link to="/admin/customers" onClick={close}>{t("navCustomers")}</Link>
            <Link to="/admin/analytics" onClick={close}>{t("navAnalytics")}</Link>
          </>
        )}
        {isAdmin && (
          <>
            <Link to="/admin/products" onClick={close}>{t("navProducts")}</Link>
            <Link to="/admin/promotions" onClick={close}>{t("navPromotions")}</Link>
            <Link to="/admin/pricing" onClick={close}>{t("navPricing")}</Link>
            <Link to="/admin/inventory" onClick={close}>{t("navInventory")}</Link>
            <Link to="/admin" onClick={close}>{t("navApprovals")}</Link>
          </>
        )}
        <button onClick={logout}>{t("logOut")}</button>
      </div>
    </nav>
  );
}

// localStorage-safe serializer: Firestore Timestamps become epoch millis,
// which utils/promo's toMillis() already understands on the way back.
function cartReplacer(_, v) {
  return v && typeof v === "object" && typeof v.toMillis === "function" ? v.toMillis() : v;
}

function AppRoutes() {
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  // Rep/admin "Ordering for" selection — lives here so it survives navigating
  // between catalog (barcode scanning) and cart during one ordering session.
  const [orderFor, setOrderFor] = useState(null);
  // The "Note for Sunjin" text lives here too so it survives navigation.
  const [orderComment, setOrderComment] = useState("");
  // Paste-imported flag → keepWrittenOrder on submit (QB lines as-written).
  // Lived in Cart page state until 2026-09-01: navigating away mid-order
  // (e.g. to /admin/pricing to enter specials) silently dropped it and the
  // invoice reverted to storage sort. Now it rides with the cart itself.
  const [pasteImported, setPasteImported] = useState(false);
  const [cartRestored, setCartRestored] = useState(false);

  // The whole in-progress order PERSISTS per account (Brian, 2026-07-14):
  // getting bounced to another page, refreshing, or closing the browser no
  // longer loses a half-built field order. Cleared on successful submit.
  useEffect(() => {
    setCartRestored(false);
    if (!user) { setCartItems([]); setOrderFor(null); setOrderComment(""); setPasteImported(false); return; }
    try {
      const saved = JSON.parse(localStorage.getItem(`sunjin-cart-${user.uid}`) || "null");
      setCartItems(saved?.items || []);
      setOrderFor(saved?.orderFor || null);
      setOrderComment(saved?.comment || "");
      setPasteImported(saved?.pasteImported === true);
    } catch {
      setCartItems([]); setOrderFor(null); setOrderComment(""); setPasteImported(false);
    }
    setCartRestored(true);
  }, [user?.uid]); // restore runs per-account, keyed by uid only

  useEffect(() => {
    if (!user || !cartRestored) return; // never clobber storage before restore
    try {
      localStorage.setItem(
        `sunjin-cart-${user.uid}`,
        JSON.stringify({ items: cartItems, orderFor, comment: orderComment, pasteImported }, cartReplacer)
      );
    } catch { /* storage full/blocked — cart still works, just not persisted */ }
  }, [user, cartRestored, cartItems, orderFor, orderComment, pasteImported]);

  // Total cases across the cart — shown on the navbar cart badge.
  const cartCount = cartItems.reduce((sum, i) => sum + i.qty, 0);

  function loadCart(items) {
    setCartItems(items);
    setPasteImported(false); // reorder/edit rebuilds aren't paste imports
  }

  // Staff edit-any-order (Brian, 2026-08-24): admin Orders cancels a pending
  // order and rebuilds it here — items, the store it was for, and the
  // customer's note — replacing whatever was in the cart.
  function loadCartForEdit(items, orderForSel, comment, keepWritten = false) {
    setCartItems(items);
    setOrderFor(orderForSel ?? null);
    setOrderComment(comment || "");
    // Editing a paste-imported order keeps its as-written QB line order on
    // resubmit (the rebuilt cart lines are already in that order).
    setPasteImported(keepWritten === true);
  }

  function addToCart(product, qty = 1) {
    setCartItems(prev => {
      const existing = prev.find(i => i.sku === product.sku);
      if (existing) {
        return prev.map(i => i.sku === product.sku ? { ...i, qty: i.qty + qty } : i);
      }
      return [...prev, { ...product, qty }];
    });
  }

  return (
    <>
      <IdleLogout />
      <NavBar cartCount={cartCount} />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        <Route path="/catalog" element={
          <ProtectedRoute allowedRoles={["customer", "rep", "admin"]}>
            <Catalog onAddToCart={addToCart} />
          </ProtectedRoute>
        } />

        <Route path="/cart" element={
          <ProtectedRoute allowedRoles={["customer", "rep", "admin"]}>
            <Cart items={cartItems} setItems={setCartItems} orderFor={orderFor} setOrderFor={setOrderFor} comment={orderComment} setComment={setOrderComment} onAddItem={addToCart} pasteImported={pasteImported} setPasteImported={setPasteImported} />
          </ProtectedRoute>
        } />

        <Route path="/my-orders" element={
          <ProtectedRoute allowedRoles={["customer", "rep", "admin"]}>
            <MyOrders onLoadCart={loadCart} />
          </ProtectedRoute>
        } />

        <Route path="/presentation" element={
          <ProtectedRoute allowedRoles={["rep", "admin"]}>
            <PresentationSheet />
          </ProtectedRoute>
        } />

        <Route path="/admin" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminApprovals />
          </ProtectedRoute>
        } />

        <Route path="/admin/products" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminProducts />
          </ProtectedRoute>
        } />

        <Route path="/admin/pricing" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminPricing />
          </ProtectedRoute>
        } />
        <Route path="/admin/promotions" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminPromotions />
          </ProtectedRoute>
        } />

        <Route path="/admin/inventory" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminInventory />
          </ProtectedRoute>
        } />

        <Route path="/admin/barcodes" element={
          <ProtectedRoute allowedRoles={["rep", "admin"]}>
            <AdminBarcodeMapping />
          </ProtectedRoute>
        } />

        <Route path="/admin/orders" element={
          <ProtectedRoute allowedRoles={["rep", "admin"]}>
            <AdminOrders onEditToCart={loadCartForEdit} />
          </ProtectedRoute>
        } />

        <Route path="/packing/:orderId" element={
          <ProtectedRoute allowedRoles={["rep", "admin"]}>
            <PackingSheet />
          </ProtectedRoute>
        } />

        <Route path="/packing-bulk" element={
          <ProtectedRoute allowedRoles={["rep", "admin"]}>
            <BulkPackingSheets />
          </ProtectedRoute>
        } />

        <Route path="/admin/customers" element={
          <ProtectedRoute allowedRoles={["rep", "admin"]}>
            <AdminCustomers />
          </ProtectedRoute>
        } />

        <Route path="/admin/analytics" element={
          <ProtectedRoute allowedRoles={["rep", "admin"]}>
            <AdminAnalytics />
          </ProtectedRoute>
        } />

        <Route path="/" element={<HomeRedirect />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <UpdateNotifier />
          </AuthProvider>
        </BrowserRouter>
      </LanguageProvider>
    </ErrorBoundary>
  );
}
